import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEO from '@/components/base/SEO';
import { articles } from '@/mocks/asmr';

function renderMarkdown(content: string) {
  const lines = content.split('\n');
  const elements: JSX.Element[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('# ') && !line.startsWith('## ')) {
      elements.push(
        <h1 key={i} className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-8 mb-4">
          {line.replace('# ', '')}
        </h1>
      );
    } else if (line.startsWith('## ')) {
      elements.push(
        <h2 key={i} className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mt-6 mb-3">
          {line.replace('## ', '')}
        </h2>
      );
    } else if (line.startsWith('### ')) {
      elements.push(
        <h3 key={i} className="font-heading text-lg md:text-xl font-semibold text-foreground-800 mt-5 mb-2">
          {line.replace('### ', '')}
        </h3>
      );
    } else if (line.startsWith('- **')) {
      const match = line.match(/- \*\*(.+?)\*\*[：:]?\s*(.*)/);
      if (match) {
        elements.push(
          <p key={i} className="text-sm text-foreground-700 leading-relaxed ml-4 mb-1.5">
            <strong>{match[1]}</strong>：{match[2]}
          </p>
        );
      } else {
        elements.push(
          <p key={i} className="text-sm text-foreground-700 leading-relaxed ml-4 mb-1.5">
            {line.replace(/^- /, '')}
          </p>
        );
      }
    } else if (line.startsWith('- ')) {
      elements.push(
        <p key={i} className="text-sm text-foreground-700 leading-relaxed ml-4 mb-1.5">
          · {line.replace(/^- /, '')}
        </p>
      );
    } else if (line.startsWith('Q：') || line.startsWith('Q:')) {
      elements.push(
        <p key={i} className="text-sm font-semibold text-foreground-900 mt-4 mb-1">
          {line}
        </p>
      );
    } else if (line.startsWith('A：') || line.startsWith('A:')) {
      elements.push(
        <p key={i} className="text-sm text-foreground-600 leading-relaxed mb-3">
          {line}
        </p>
      );
    } else if (/^\d+\.\s/.test(line)) {
      elements.push(
        <p key={i} className="text-sm text-foreground-700 leading-relaxed ml-4 mb-1.5">
          {line}
        </p>
      );
    } else if (line.trim() === '') {
      elements.push(<div key={i} className="h-2"></div>);
    } else {
      elements.push(
        <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
          {line}
        </p>
      );
    }
    i++;
  }

  return elements;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const pageTitle = `${article.title} - ASMR免费网页`;
  const pageDesc = article.summary;
  const pageUrl = `https://asmr-free.com/article/${slug}`;

  const pageSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.summary,
      datePublished: article.date,
      author: { '@type': 'Organization', name: 'ASMR免费网页' },
      publisher: { '@type': 'Organization', name: 'ASMR免费网页' },
      inLanguage: 'zh-CN',
      url: pageUrl,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://asmr-free.com/' },
        { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://asmr-free.com/category/${article.category}` },
        { '@type': 'ListItem', position: 3, name: article.title, item: pageUrl },
      ],
    },
  ];

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={`ASMR,${article.categoryName},ASMR科普,ASMR助眠,ASMR免费网页`}
        canonicalUrl={pageUrl}
        ogType="article"
        schemaData={pageSchema}
      />
      <Navbar />
      <main>
        <article className="pt-16">
          <header className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <nav className="mb-6" aria-label="面包屑导航">
                <ol className="flex items-center flex-wrap gap-2 text-xs text-foreground-500">
                  <li><Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link></li>
                  <li><i className="ri-arrow-right-s-line"></i></li>
                  <li><Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">{article.categoryName}</Link></li>
                  <li><i className="ri-arrow-right-s-line"></i></li>
                  <li className="text-foreground-700">{article.title}</li>
                </ol>
              </nav>
              <div className="flex items-center gap-3 text-xs text-foreground-400 mb-4 flex-wrap">
                <span className="inline-block px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full font-medium">
                  {article.categoryName}
                </span>
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime}阅读</span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight">
                {article.title}
              </h1>
            </div>
          </header>

          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <div className="prose-custom">
                {renderMarkdown(article.content)}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-6">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {articles
                  .filter((a) => a.slug !== article.slug)
                  .slice(0, 2)
                  .map((related) => (
                    <Link
                      key={related.slug}
                      to={`/article/${related.slug}`}
                      className="group p-4 rounded-xl bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <span className="inline-block px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full font-medium mb-2">
                        {related.categoryName}
                      </span>
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
                        {related.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2">{related.summary}</p>
                    </Link>
                  ))}
              </div>
            </div>
          </section>
        </article>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}